<template>
  <div class="echart-box">
    <div class="echart-box-header">
      <div class="echart-box-header-type">
        <i class="echart-box-header-type-left"></i>
        <span style="white-space: nowrap">{{ echartType }}</span>
        <el-tooltip
          v-if="echartTips"
          class="echart-tips"
          :content="echartTips"
          effect="light"
          placement="top"
        >
          <i class="icon iconfont icon-help"></i>
        </el-tooltip>
      </div>
      <div class="echart-box-header-filter">
        <DatePicker
          v-if="needFilterType === 'picker'"
          :areaId="areaId"
          width="230px"
          :needText="false"
          @changeStartAndEndTime="changeStartAndEndTime"
        />
        <DateUnit
          v-else-if="needFilterType === 'unit'"
          v-model="unitType"
          :needText="false"
        />
        <div v-else-if="needFilterType === 'statType'" class="stat-type">
          <div
            @click="onStatType('history')"
            :class="[
              'stat-type-item',
              statType === 'history' && 'stat-type-active',
            ]"
          >
            历史
          </div>
          <div
            @click="onStatType('now')"
            :class="[
              'stat-type-item',
              statType === 'now' && 'stat-type-active',
            ]"
          >
            现有
          </div>
        </div>
      </div>
    </div>
    <div
      :style="{ height: `${boxHeight - 50}px` }"
      class="echart-box-main"
      v-loading="!show"
    >
      <Echart
        v-if="show"
        :el="echartEl"
        :hasStat="hasStat"
        :options="echartOptions"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import moment from 'moment'
import DatePicker from './DatePicker'
import DateUnit from './DateUnit'
import Echart from './Echart'
import constant from '@/utils/constant'

export default {
  name: 'EchartBox',
  components: {
    DatePicker,
    DateUnit,
    Echart,
  },
  props: {
    areaId: [Number, String],
    regionId: [Number, String],
    echartType: {
      type: String,
      default: '',
    },
    echartTips: {
      type: String,
      default: '',
    },
    needFilterType: {
      type: String,
      default: '',
    },
    boxHeight: {
      type: Number,
      default: 500,
    },
    getStatFunc: {
      type: Function,
      default() {
        return () => {}
      },
    },
    routeName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      startAndEndTime: [],
      unitType: '',
      statType: '',
      show: true,
      statOptions: {},
    }
  },
  computed: {
    ...mapGetters({
      isLarge: 'customer/isLarge',
      userDetail: 'customer/userDetail',
    }),
    echartEl() {
      return this.statOptions.echartEl
    },
    hasStat() {
      return this.statOptions.hasStat
    },
    echartOptions() {
      return this.statOptions.echartOptions
    },
    agentAreas() {
      return this.userDetail.agentAreas || []
    },
    formatStartAt() {
      let startAt = constant.startDate
      if (this.areaId) {
        const curArea = _.find(this.agentAreas, item => item.id === this.areaId)
        if (moment(curArea.beginAt) > moment(startAt)) {
          startAt = curArea.beginAt
        }
      }
      return moment(startAt).format('YYYY-MM-DD')
    },
  },
  watch: {
    async unitType(unit) {
      let startTime = ''
      let endTime = ''
      const yesterdayDate = moment().subtract(1, 'days').format('YYYY-MM-DD')
      if (unit === 'day') {
        startTime = moment().subtract(7, 'days').format('YYYY-MM-DD')
        endTime = yesterdayDate
      }
      if (unit === 'week') {
        startTime = moment().subtract(8, 'weeks').format('YYYY-MM-DD')
        endTime = yesterdayDate
      }
      if (unit === 'month') {
        startTime = moment().startOf('year').format('YYYY-MM-DD')
        endTime = yesterdayDate
      }
      if (startTime < this.formatStartAt) {
        startTime = this.formatStartAt
      }
      const params = {
        regionId: this.regionId,
        unit,
        startTime,
        endTime,
      }
      this.onGetStatFunc(params)
    },
    isLarge() {
      if (this.$route.name === this.routeName) {
        this.refreshChart()
      }
    },
    statType(type) {
      const params = {
        regionId: this.regionId,
        type,
      }
      this.onGetStatFunc(params)
    },
  },
  created() {
    if (this.needFilterType === 'none') {
      return
    }
    if (this.needFilterType === '') {
      const yesterdayDate = moment().subtract(1, 'days').format('YYYY-MM-DD')
      const params = {
        regionId: this.regionId,
        startTime: yesterdayDate,
        endTime: yesterdayDate,
      }
      this.onGetStatFunc(params)
    }
    if (this.needFilterType === 'unit') {
      this.unitType = 'day'
    }
    if (this.needFilterType === 'statType') {
      this.statType = 'history'
    }
  },
  methods: {
    changeStartAndEndTime(times) {
      this.startAndEndTime = times
      const params = {
        regionId: this.regionId,
        startTime: times[0],
        endTime: times[1],
      }
      this.onGetStatFunc(params)
    },
    onStatType(type) {
      if (this.statType !== type) {
        this.statType = type
      }
    },
    async onGetStatFunc(params) {
      this.hideChart()
      this.statOptions = await this.getStatFunc(params)
      this.showChart()
    },
    refreshChart() {
      this.hideChart()
      this.showChart()
    },
    hideChart() {
      this.show = false
    },
    showChart() {
      this.$nextTick(() => {
        this.show = true
      })
    },
  },
}
</script>

<style lang='scss'>
.echart-box {
  width: 100%;
  height: 100%;
  background: #fff;
  box-shadow: 0px 0px 10px 0px rgba(92, 92, 93, 0.1);
  border-radius: 4px;
  .echart-box-header {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    padding: 0 16px;
    .echart-box-header-type {
      display: flex;
      align-items: center;
      color: #333;
      .echart-box-header-type-left {
        background-color: #2397f3;
        border-radius: 2px;
        width: 4px;
        height: 15px;
        margin-right: 10px;
      }
      .echart-tips {
        margin-top: -8px;
        color: #2397f3;
        font-size: 12px;
        margin-left: 4px;
      }
    }
  }
  .echart-box-main {
    border-top: 1px solid #e8e8e8;
  }
  .stat-type {
    display: flex;
    border-radius: 4px;
    overflow: hidden;
    .stat-type-item {
      padding: 4px 10px;
      white-space: nowrap;
      cursor: pointer;
      background: #eef0f2;
      color: #b1b1b2;
      font-size: 14px;
    }
    .stat-type-active {
      background: #2397f3;
      color: #fff;
    }
  }
}
</style>